import * as React from 'react';
import { 
    AppliedFilterBarOpt, 
    FILTER_BAR_TYPE, 
    FilterBarInputOpt, 
    FilterGroup,         
    IFilterBar 
} from '../../models/utilities/filter-bar';
import { PageData } from '../../models/utilities/paged-request';
import { GlobalConstants } from '../../utils/app-constants';
import { toShortDate } from '../../utils/date-utils';
import { FilterBar } from '../filter-bar/FilterBar';

// #region Component State and Props

interface IProps {
    filterProps                     : IFilterBar;
    onFilterUpdate                  : any;
    searchCallback                  : any;
    id?                             : string;
    guardCallback?                  : any;
    onReset?                        : any;
}

// #endregion

export const IsolatedFilterBar = (props : IProps) => {

    const [isInitialLoad, setIsInitialLoad] = React.useState(false);
    const [hasPendingFilters, setHasPendingFilters] = React.useState(false);

    // Fire off search when props change    
    React.useEffect(() => {
        if (!hasPendingFilters) {
            onSearch();
        }
    }, [props.filterProps]);

    /**
     * Handle the search command.  
     * Checks if there's a guard available.
     * If there is a guard, call it and wait on the result
     * before continuing.
     */
    const onSearch = async (overrideFilters? : IFilterBar) => {

        let canContinue = true;
        if (props.guardCallback) {
            canContinue = await props.guardCallback();
        }

        if (canContinue) {        
            
            const tmp = updateAppliedFilters(overrideFilters);

            if (!tmp.paginationInfo.reusePaging) {
                tmp.paginationInfo = new PageData(GlobalConstants.DEFAULT_PAGE_SIZE);
            }
            else {
                tmp.paginationInfo.reusePaging = false;
            }
            
            props.searchCallback(tmp);

            if (overrideFilters === undefined) {
                setHasPendingFilters(false);
            }
            else {
                setHasPendingFilters(true);
            }

        }
    };

    /**
     * Check and update what filters are applied
     * @param overrideFilters Possible list of override filters to use instead of props
     */
    const updateAppliedFilters = (overrideFilters? : IFilterBar) => {

        if (overrideFilters && overrideFilters.hasOwnProperty('target')) {
            return props.filterProps;
        }

        const tmp = (overrideFilters) ? overrideFilters : props.filterProps;
        tmp.appliedFilters = [];

        if (tmp.filters) {
            tmp.filters.forEach((itm: FilterGroup) => {            
                itm.opts.forEach((subitm: FilterBarInputOpt) => {
                    if ((itm.type === 'text'  || itm.type === 'numeric' || itm.type === 'dealer') && subitm.data.length > 0) {
                        tmp.appliedFilters.push({
                            uuid : subitm.uuid,
                            label : `${ itm.name } : ${ subitm.data }`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.data
                        });
                    }
                    else if (itm.type === 'autocomplete' && subitm.data) {
                        tmp.appliedFilters.push({
                            uuid : subitm.uuid,
                            label : `${ itm.name } : ${ subitm.data.name }`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.data.id
                        });
                    }
                    else if (itm.type === 'checkboxes' && subitm.data === true) {
                        tmp.appliedFilters.push({
                            uuid : subitm.uuid,
                            label : `${ itm.name } : ${ subitm.label }`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.qsValue
                        });
                    }
                    else if (itm.type === 'dropdown' && subitm.data) {
                        tmp.appliedFilters.push({
                            uuid : subitm.uuid,
                            label : `${ itm.name } : ${ subitm.label }`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.qsValue
                        });
                    }
                    else if (itm.type === 'date' && subitm.data !== undefined) {
                        tmp.appliedFilters.push({
                            uuid : subitm.uuid,
                            label : `${ itm.name } : ${ toShortDate(subitm.data) }`,                            
                            qsName: itm.qsParamName,
                            qsValue: toShortDate(subitm.data)
                        });
                    }
                    else if (itm.type === 'dateplus' && subitm.data !== undefined && subitm.data.selectedDate !== undefined && subitm.data.selectedDate.qsValue !== undefined) {
                        tmp.appliedFilters.push({
                            uuid : subitm.uuid,
                            label : `${ itm.name } : ${ subitm.data.checkboxValue.qsValue === true ? 'DOW' : '<=' } ${ toShortDate(subitm.data.selectedDate.qsValue) }`,                            
                            qsName: itm.qsParamName,
                            qsValue: `${toShortDate(subitm.data.selectedDate.qsValue)}&${subitm.data.checkboxValue.qsName}=${subitm.data.checkboxValue.qsValue}`
                        });
                    }
                });
            });
        }

        return tmp;
    };

    /**
     * 
     * @param propName 
     * @param optId 
     * @param newState 
     * @param propType 
     */
    const onFilterUpdate = (propName : string, optId : string, newState : any, propType: FILTER_BAR_TYPE) => {

        const tmp = {...props.filterProps} as IFilterBar;
        tmp.filters.forEach((itm: FilterGroup) => {
            if (itm.name === propName) {
                itm.opts.forEach((subitm: FilterBarInputOpt) => {
                    // For dropdown types, we only want one value to be selected at a time
                    if (propType === 'dropdown') {                    
                        subitm.data = (subitm.uuid === optId) ? newState : false;
                    }
                    else if (propType === 'dateplus') {
                        if (Object.prototype.toString.call(newState) === '[object Date]') {
                            subitm.data.selectedDate.qsValue = newState;
                        }
                        else {
                            subitm.data.checkboxValue.qsValue = newState;
                        }
                    }
                    else if (subitm.uuid === optId) {
                        subitm.data = newState;
                    }
                });
            }
        });

        setHasPendingFilters(true);
        props.onFilterUpdate(tmp);
    };

    const onFilterDelete = (uid : string) => {

        let tmp = {...props.filterProps} as IFilterBar;

        tmp.filters.forEach((itm: FilterGroup) => {
            itm.opts.forEach((subitm: FilterBarInputOpt) => {
                if (subitm.uuid === uid) {
                    switch (itm.type) {
                        case 'text':
                        case 'dealer':
                        case 'autocomplete':
                        case 'numeric':
                            subitm.data = '';
                            break;
                        case 'checkboxes':
                        case 'dropdown':
                            subitm.data = false;
                            break;
                        case 'date':
                            subitm.data = undefined;
                            break;
                        case 'dateplus':
                            subitm.data.selectedDate = {
                                qsValue: undefined,
                                qsName: 'maxCutoff'
                            };
                            subitm.data.checkboxValue = {
                                qsValue: true,
                                qsName: 'matchDayOfWeek'
                            };
                            break;
                    }
                }
            });
        });

        tmp = findAndRemoveAppliedFilter(uid, tmp);

        if (tmp.appliedFilters.length === 0) {
            // tmp.clearAll();
        }

        onSearch(tmp);
    };

    const onFilterReset = () => {        
        setHasPendingFilters(false);
        props.onReset();
    };

    const findAndRemoveAppliedFilter = (uid : string, filters : IFilterBar) => {

        const idx = filters.appliedFilters.findIndex((itm : AppliedFilterBarOpt) => {
            return itm.uuid === uid;
        });

        if (idx > -1) {
            filters.appliedFilters.splice(idx, 1);
        }

        return filters;
    };

    return <div className="row" id={ props.id }>
                <div className="col">
                    <FilterBar
                        onApplyFilters={ onSearch }
                        onUpdateFilters={ onFilterUpdate }
                        onFilterDelete={ onFilterDelete }
                        onFilterReset={ onFilterReset }
                        filterProps={ props.filterProps }
                    />
                </div>
            </div>;

};
